if (!customElements.get('de-hero-carousel')) {
  customElements.define('de-hero-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // Keep the hero signup confirmation clear of the existing welcome offer.
      if (window.location.hash.startsWith('#DETrashRound-') && this.querySelector('[data-signup-result]')) {
        document.querySelector('.de-signup-offer')?.classList.remove('is-visible');
      }
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('.de-hero-slide')];
      this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      const controls = this.querySelector('.de-hero-controls');
      if (!controls || this.slides.length < 2) return;
      controls.hidden = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || Boolean(this.querySelector('[data-signup-result]'));
      this.pauseButton = this.querySelector('[data-autoplay]');
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', String(i !== this.index));
        slide.hidden = false;
        const video = slide.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.addEventListener('ended', () => {
          if (i === this.index && !this.paused) this.show(this.index + 1, false);
        }, options);
        video.addEventListener('error', () => {
          if (i === this.index) this.show(this.index + 1, false);
        }, options);
      });
      this.updatePlayback();
      this.addEventListener('focusin', event => {
        if (event.target.closest('[data-autoplay]')) return;
        this.paused = true;
        this.updatePlayback();
      }, options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      this.motion.addEventListener('change', () => {
        this.paused = this.motion.matches;
        this.updatePlayback();
      }, options);
      controls.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-autoplay')) {
          this.paused = !this.paused;
          this.updatePlayback();
          return;
        }
        this.show(button.hasAttribute('data-slide') ? Number(button.dataset.slide) : this.index + Number(button.dataset.direction));
      }, options);
      controls.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.addEventListener('touchstart', event => {
        this.touch = event.touches.length === 1 && !event.target.closest('a, button') ? event.touches[0] : null;
      }, { ...options, passive: true });
      this.addEventListener('touchend', event => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.clientX;
        const dy = event.changedTouches[0].clientY - this.touch.clientY;
        this.touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
      }, { ...options, passive: true });
      this.addEventListener('touchcancel', () => { this.touch = null; }, options);
    }
    stop() {
      window.clearTimeout(this.timer);
      this.slides?.forEach(slide => slide.querySelector('video')?.pause());
    }
    start() {
      this.stop();
      if (this.paused || document.hidden) return;
      const video = this.slides[this.index].querySelector('video');
      if (video) {
        const currentIndex = this.index;
        if (video.ended) video.currentTime = 0;
        // A bounded fallback keeps the photographs available if media stalls.
        this.timer = window.setTimeout(() => this.show(this.index + 1, false), 11000);
        video.play().catch(() => {
          if (this.index === currentIndex && !this.paused && !document.hidden) this.show(this.index + 1, false);
        });
      } else {
        this.timer = window.setTimeout(() => this.show(this.index + 1, false), 6000);
      }
    }
    updatePlayback() {
      this.pauseButton.textContent = this.paused ? '\u25b6' : '\u275a\u275a';
      this.pauseButton.setAttribute('aria-label', this.paused ? 'Play automatic slideshow' : 'Pause automatic slideshow');
      this.querySelector('.de-hero-status').setAttribute('aria-live', this.paused ? 'polite' : 'off');
      this.start();
    }
    show(index, announce = true) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', String(i !== this.index));
        const video = slide.querySelector('video');
        if (video) {
          video.pause();
          if (i === this.index) video.currentTime = 0;
        }
      });
      this.querySelectorAll('[data-slide]').forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.index)));
      if (announce) this.querySelector('.de-hero-status').textContent = this.slides[this.index].getAttribute('aria-label');
      this.start();
    }
    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
